import * as Sentry from "@sentry/browser";
import React from "react";

import {
  apiFailureTags,
  declaredReportingGroup,
  toApiFailureFields,
  toReportableRequest,
} from "./api_failure";
import { isBrowserTeardownError } from "./browser_teardown";
import { isChunkLoadError } from "./chunk_load_error";
import { catalogRouteTags } from "./catalog_route";
import { useConfig } from "./config";
import { clientLoadsStylesheets } from "./css_preload_recovery";
import { getErrorMessage } from "./error_message";
import { SET_PASSWORD_PATH } from "./password_link_token";
import {
  isDetachedPaymentFrameError,
  isInAppBrowserInjectedError,
  isMissingVendorGlobalError,
  isThirdPartyTagError,
} from "./third_party_tag";

const isProduction = import.meta.env.PROD;

// Fraction of chunk-load misses to keep in Sentry. Start at 1 to measure the
// true failure rate; lower it only if the volume proves noisy.
const CHUNK_LOAD_ERROR_SAMPLE_RATE = 1;

// Fraction of stylesheet-preload failures from clients that load no stylesheets
// at all. Kept as its own sampled group rather than dropped: the population is
// worth counting, and a real regression that happens to look like it stays
// visible — but left whole it outnumbers the actionable reports several times
// over and buries them, which is the whole reason it is split out.
const CSS_PRELOAD_CLIENT_BLOCKED_SAMPLE_RATE = 0.05;

// One dead request, worded per engine. All three are transient network blips
// with no resolvable stack, so we drop them rather than report them — and they
// have to be matched together: covering one engine's wording alone filters the
// common case while leaving the others free to page us, which then reads as a
// fault specific to a browser rather than the engine-agnostic blip it is.
//
// Matched whole rather than as a substring. "load failed" occurs inside the
// chunk-load and stylesheet-preload messages, and those groups are deliberate
// signal — a substring test would swallow them as soon as one arrived carrying
// a real exception, and would leave this branch silently dependent on running
// after the chunk-load branch above.
//
// This also can't be moved to ignoreErrors: that list substring-matches and
// runs before beforeSend, so it would take the Chromium chunk-load message
// ("failed to fetch dynamically imported module") with it.
//
// Matching whole depends on the messages still being bare by the time they
// reach us, which is what enhanceFetchErrorMessages guarantees below — the
// SDK's default appends the request host to these exact three wordings, in
// place on the Error, and a suffixed message fails every test here.
const BARE_FETCH_FAILURE_MESSAGES = [
  "failed to fetch", // Chromium
  "load failed", // WebKit
  "networkerror when attempting to fetch resource.", // Gecko
];

function isBareFetchFailure(error: unknown): boolean {
  const normalized = getErrorMessage(error).toLowerCase().trim();
  return BARE_FETCH_FAILURE_MESSAGES.includes(normalized);
}

// The third-party accessibility overlay rejects its own asset fetches with a
// plain object ({ code, status, type, url }) rather than an Error. Sentry then
// captures a stackless "Object captured as promise rejection" with no message
// or frame to key on — so neither denyUrls (needs a frame URL) nor the
// message-based fetch-failure drop can reach it. Match the overlay's origin on
// the rejected object's own url instead.
function isThirdPartyOverlayRejection(error: unknown): boolean {
  const { url } = toApiFailureFields(error);

  return !!url && /nagich\.co\.il/i.test(url);
}

/**
 * Name the person behind a report. Without it an event about one person's
 * session is anonymous, and a failure nobody can attribute cannot be acted on:
 * there is no way to tell whose access broke, or even how many people it was.
 */
export function identifyToSentry(
  user: { sub?: string; email?: string } | undefined
) {
  if (!user) return;

  Sentry.setUser({ id: user.sub, email: user.email });
}

// Sentry beforeSend for the booking site. Chunk-load misses are collapsed into
// one fingerprinted group — the release SHA rides along on event.release (set by
// @sentry/vite-plugin) and stays filterable within the group — so the genuine
// chunk-failure rate becomes visible. Everything else passes through unchanged,
// apart from the bare fetch failures and the third-party script failures we drop.
export function processSentryEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint
): Sentry.ErrorEvent | null {
  const error = hint.originalException;

  // Tag catalog-route events with partner context so catalog errors — render-phase
  // or async (which React error boundaries never see) — alert with the same
  // partner_slug/route as blank renders. Existing tags win, so an explicitly
  // tagged blank-render event keeps its own values.
  const catalogTags = catalogRouteTags(window.location.pathname);
  if (catalogTags) {
    event.tags = { ...catalogTags, ...event.tags };
  }

  if (isChunkLoadError(error)) {
    if (Math.random() >= CHUNK_LOAD_ERROR_SAMPLE_RATE) {
      return null;
    }
    event.fingerprint = ["chunk-load-error"];
    event.tags = {
      ...event.tags,
      chunk_load_error: true,
      online: navigator.onLine,
    };
    return event;
  }

  // The stylesheet-preload reporter raises these deliberately, so the tag it
  // sets is what identifies them here — there is no exception to match on. Only
  // the client that fetches no stylesheets at all is split off; a failure on a
  // client whose other stylesheets loaded names a genuinely unreachable asset
  // and stays in the group that gets looked at.
  if (event.tags?.css_preload_error && !clientLoadsStylesheets()) {
    if (Math.random() >= CSS_PRELOAD_CLIENT_BLOCKED_SAMPLE_RATE) {
      return null;
    }
    event.message = "Stylesheet loading blocked by client";
    event.fingerprint = ["css-preload-client-blocked"];
    event.tags = { ...event.tags, css_preload_client_blocked: true };
    return event;
  }

  // A failure that knows it is the same fault wherever it was raised says so,
  // and is filed under the group it names. Reporting otherwise groups by where
  // an exception was thrown from, which scatters one such fault across every
  // call site that can raise it — each looking like a small, separate problem,
  // and none showing the rate.
  //
  // Asking the raiser rather than recognising each fault here is what keeps
  // this one branch instead of one per fault: nothing below needs to know which
  // failures declare a group, only that a declared one is honoured.
  //
  // Returned rather than left to the drops below, for the reason the deliberate
  // captures above are: a failure deliberate enough to name its own group is
  // never the unattributable noise those drops target.
  const reportingGroup = declaredReportingGroup(error);
  if (reportingGroup) {
    const fields = toApiFailureFields(error);
    // An existing fingerprint wins, for the reason the extras below do: a
    // handler that already named a group chose it knowing what it was
    // capturing, which is more than can be read off the exception here. The
    // event is still returned either way, so a deliberate capture keeps its own
    // group without falling back into the drops below.
    if (!event.fingerprint?.length) {
      event.fingerprint = [reportingGroup];
    }
    // New tags win, as on the chunk-load group above: these describe the
    // failure reporting just identified, so they are the more specific account.
    event.tags = { ...event.tags, ...apiFailureTags(fields) };
    // Which record failed, which the endpoint tag generalises away — redacted
    // rather than raw, because some of these requests name a credential.
    // Existing extras win, so a handler that already named the request — and
    // knows more about it than this can — keeps its own account.
    if (fields.url) {
      event.extra = { url: toReportableRequest(fields.url), ...event.extra };
    }
    return event;
  }

  // An analytics provider that fails to initialize is reported deliberately by
  // the analytics combinator, which drops that client and keeps the page
  // serving. The throw happens inside the vendor's own script, so the
  // third-party drop below would discard exactly the report we asked for and a
  // partner's broken container would be invisible. Only our own capture carries
  // this tag; a container error that merely reaches the global handler has none
  // and is still dropped.
  if (event.tags?.analytics_provider) {
    return event;
  }

  // Same shape as above, for the profile fetch behind the dashboard's route
  // gate. A profile that never arrives strands a partner admin on a screen, and
  // when it never arrives because the request itself died the thrown TypeError
  // reads exactly "Failed to fetch" — so the bare-fetch drop below would
  // discard the one bucket that failure belongs to and the outage would be
  // invisible. Only that deliberate capture sets this tag.
  if (event.tags?.partner_user_status) {
    return event;
  }

  if (
    isBareFetchFailure(error) ||
    isThirdPartyOverlayRejection(error) ||
    isBrowserTeardownError(event) ||
    isInAppBrowserInjectedError(event) ||
    isDetachedPaymentFrameError(event) ||
    // Two independent nets over the same container failures, neither cheaper
    // than the other: this one covers a container served first-party whose URL
    // we have no way to recognise yet, the origin test below every container
    // whose URL we do.
    isMissingVendorGlobalError(event) ||
    isThirdPartyTagError(event)
  ) {
    return null;
  }

  return event;
}

interface SentryProps {
  children?: React.ReactNode;
}

// Sentry initialises once per page, and the guard for that has to be module
// state: the replay integration refuses a second instance through a module-level
// flag it sets in its own constructor and never resets, so a guard scoped to a
// component instance is reset by a remount and lets a second init through. That
// second init raises while the integration list is still being built — before
// init can act on `enabled` — from inside a passive effect mounted above every
// error boundary, which React escalates to the root, unmounting the whole app.
let initialized = false;

const SENTRY_OPTIONS: Sentry.BrowserOptions = {
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  ignoreErrors: [
    "TypeError: Cannot read properties of undefined (reading 'spaceID')",
    "jQuery",
    "Chartboost",
    // Browser-extension noise: an injected content script rejects a promise
    // that our global onunhandledrejection handler then captures. Not app
    // code — no resolvable stacktrace — so it only inflates error counts.
    // Matched on the extension's signature string, not the generic
    // "Non-Error promise rejection" prefix, so real app rejections still report.
    "Object Not Found Matching Id",
    // The browser telling itself that a resize callback resized something
    // again, so it deferred the next pass to the following frame. It is a
    // notification, not a failure: nothing is left unpainted and there is
    // no frame of ours on it — the spec asks engines to surface it, and
    // ours reaches the global handler as an error with nobody to act on.
    //
    // Left as a substring on purpose, unlike the whole-message drops in
    // beforeSend, and filtered here rather than there because it needs
    // none of what beforeSend offers: no caught-versus-unhandled gate,
    // since our code never raises this and so can never be reporting it
    // deliberately. The known engine wordings differ only in their tail
    // and no failure of ours can name a ResizeObserver loop, so the shared
    // prefix covers both and any future rewording without a second entry.
    // One message currently spread across several issues, which is what
    // resolving them one at a time could never fix.
    "ResizeObserver loop",
  ],
  beforeSend: processSentryEvent,
  // The browser keeps its own record of the address a page opened at, which no
  // code can rewrite, and page-load traces are named from it. For this page that
  // address held a password link's token, so its traces are never sent.
  ignoreTransactions: [SET_PASSWORD_PATH],
  // Required by BARE_FETCH_FAILURE_MESSAGES above: keeps the request host
  // off the thrown Error's message, where the default would append it and
  // break that whole-message drop. Costs no diagnostic detail — the SDK
  // still appends the host to the reported exception value, so only the
  // Error our own code reads stays bare. Dropping to false would suppress
  // the host everywhere instead, including in Sentry's own titles.
  enhanceFetchErrorMessages: "report-only",
  // Third-party accessibility overlay whose async asset fetches reject as
  // "Failed to fetch". They surface as unhandled rejections we can't act on
  // and never come from a resolvable Error, so beforeSend's message match
  // can't reach them — drop them here by their script origin instead.
  denyUrls: [/localhost/i, /nagich\.co\.il/i],
  enabled: isProduction,
};

interface SentryInitConfig {
  dsn: string;
  environment: string;
}

// Nothing this setup can do is worth the page. It runs from a passive effect
// above every error boundary, so a throw here reaches the root and unmounts the
// whole app — which is how the second init blanked a checkout for 202 users. The
// flag above closes that one trigger; the outer catch closes the class, so an
// integration that raises during setup costs telemetry rather than the booking.
function initSentry({ dsn, environment }: SentryInitConfig) {
  try {
    const tracing = Sentry.browserTracingIntegration();
    // Built once and shared by both attempts below: rebuilding it would hit the
    // single-instance refusal described above.
    const replay = Sentry.replayIntegration({
      blockAllMedia: true,
      maskAllText: false,
      maskAllInputs: false,
    });

    try {
      Sentry.init({
        ...SENTRY_OPTIONS,
        dsn,
        environment,
        integrations: [tracing, replay],
      });
    } catch (error) {
      // The client is bound before its integrations are, so a setup that raises
      // still leaves a client reporting errors. What it loses is the rest of
      // the setup: integrations install in two passes, every setup() and then
      // every afterAllSetup(), and a throw in the first escapes before the
      // second is reached — so no integration's afterAllSetup runs at all.
      // Ordering is no defence: session replay does all of its own work in
      // that later pass, so it is lost wherever it sits in the list. Its
      // sample rates are zero because it is started by hand on the payment
      // path, and an integration that never set up is absent from the client —
      // leaving that start call with nothing to start, so the sessions we most
      // want recorded record nothing. Tracing is both what raises — in-app browsers
      // inject scripts that patch event registration in ways its listener setup
      // cannot survive — and the only entry worth giving up, so init again
      // without it and let the rest install. The tag keeps these degraded
      // sessions distinguishable from ones where tracing simply recorded
      // nothing.
      try {
        Sentry.init({
          ...SENTRY_OPTIONS,
          dsn,
          environment,
          integrations: [replay],
        });
        Sentry.setTag("tracing", "disabled");
      } finally {
        // Reported from a finally so the failure that cost us tracing is on
        // the record even when the retry raises in its turn. The retry has a
        // way of its own to fail — session replay's setup runs for the first
        // time on that attempt, under the same patched globals that broke
        // tracing — and without this the outer catch would report only that
        // second error. The first would then stop arriving while the crash it
        // names carried on, which reads as fixed.
        Sentry.captureException(error);
      }
    }
  } catch (error) {
    // Warned before it is reported, because this is the branch where reporting
    // is least likely to work — but still reported, since a client is bound
    // before the setup that raised and so usually survives to carry it.
    console.warn("Sentry setup failed", error);
    Sentry.captureException(error);
  }
}

export function SentryProvider(props: SentryProps) {
  const { children } = props;
  const config = useConfig();

  React.useEffect(() => {
    // A DSN-less config must not spend the single init: /web_ui_config is read
    // once per page and a client without a DSN reports nothing for its lifetime,
    // so bail and let the next config that carries one through instead.
    if (initialized || !config.sentryDSN) {
      return;
    }

    initialized = true;

    initSentry({ dsn: config.sentryDSN, environment: config.environment });
  }, [config]);

  return <>{children}</>;
}
